// src/components/WelcomeEditorialDialog.jsx
//
// One-time announcement of the Caro Editorial default (spec 20260930-caro-editorial-theme,
// T117-T121): "CARO OS — eindelijk in het finale jasje". Shown once per browser, only to a
// signed-in user on a portal page (never on /login or the public /alarm/* pages). Any way of
// closing it marks it as seen; the secondary action leads to /settings, where the theme
// switcher lives, for whoever wants Plum back.
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeSettings } from '../contexts/ThemeContext';

export const WELCOME_EDITORIAL_SEEN_KEY = 'caro_welcome_editorial_seen';

// Routes a signed-in user can still reach that are not the portal itself.
const PUBLIC_PREFIXES = [
  '/login',
  '/onboarding',
  '/activate',
  '/alarm/',
  '/approve/',
  '/health',
  '/auth-callback',
  '/auth-error',
  '/reset-password',
];

const hasSeen = () => {
  try {
    return window.localStorage.getItem(WELCOME_EDITORIAL_SEEN_KEY) === '1';
  } catch {
    // Storage blocked: treat as seen, better silent than a dialog on every page load.
    return true;
  }
};

const markSeen = () => {
  try {
    window.localStorage.setItem(WELCOME_EDITORIAL_SEEN_KEY, '1');
  } catch {
    /* storage blocked: nothing to remember it in */
  }
};

const WelcomeEditorialDialog = () => {
  const { isAuthenticated } = useAuth();
  const { t, reducedMotion } = useThemeSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [open, setOpen] = useState(false);

  const isPublic = PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!isAuthenticated || isPublic) {
      setOpen(false);
      return;
    }
    if (!hasSeen()) setOpen(true);
  }, [isAuthenticated, isPublic]);

  const close = () => {
    markSeen();
    setOpen(false);
  };

  const chooseTheme = () => {
    close();
    navigate('/settings');
  };

  const noMotion = prefersReducedMotion || reducedMotion === true;

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      transitionDuration={noMotion ? 0 : undefined}
      aria-labelledby="welcome-editorial-title"
      aria-describedby="welcome-editorial-body"
    >
      <IconButton
        onClick={close}
        aria-label={t('WelcomeEditorial.close')}
        sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary' }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pt: { xs: 4, sm: 5 }, px: { xs: 3, sm: 5 }, pb: 2 }}>
        <Typography
          variant="overline"
          component="p"
          sx={{ color: 'secondary.main', letterSpacing: '0.12em', mb: 0.5 }}
        >
          {t('WelcomeEditorial.subtitle')}
        </Typography>
        <Typography id="welcome-editorial-title" variant="h3" component="h2" sx={{ color: 'primary.main', mb: 2 }}>
          {t('WelcomeEditorial.title')}
        </Typography>
        <Box id="welcome-editorial-body">
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 2 }}>
            {t('WelcomeEditorial.body')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('WelcomeEditorial.revert')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={chooseTheme} color="primary" sx={{ minHeight: 48 }}>
          {t('WelcomeEditorial.secondary')}
        </Button>
        <Button onClick={close} variant="contained" color="primary" autoFocus sx={{ minHeight: 48 }}>
          {t('WelcomeEditorial.primary')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeEditorialDialog;
